/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/api/api';
import { CaseService } from 'app/cm/services/case-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { Toaster } from 'app/shared/lib/toaster';
import { EssModalCustomElement } from 'app/shared/elements/ess-dialog';
import { validate } from 'app/shared/elements/ess-rule';
import * as CM from 'app/cm/models/essette-api';
import { ReferralOrderItem } from 'app/cm/admin/referral/referral-types';
import { EssSortableListCustomElement } from 'app/shared/elements/ess-sortable-list';

@autoinject
export class ReferralTypeCriteria extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable
    protected $referralTypeId: string | null = null;

    @bindable
    protected $referralTypeName: string | null = null;

    @bindable
    protected includeInactive: boolean = false;

    @bindable
    protected parentIsActive: boolean = false;

    @bindable
    protected reloadReferralType: Function = function() { };

    protected referralTypeCriteria: CM.ReferralCriteria[] = [];
    protected sorting = new Sorting();
    protected pageSize = 10;
    protected resultCount = 0;
    protected validateForm = validate;
    protected criterion: CM.ReferralCriteria = {};
    protected criteriaOrder: ReferralOrderItem[] = [];

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $criterionModal!: EssModalCustomElement;
    protected $sortCriteriaModal!: EssModalCustomElement;
    protected $criteriaList!: EssSortableListCustomElement<ReferralOrderItem>;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CaseService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected async attached(): Promise<void> {
        await this.reload();
    }

    ////****************** Events ***************************************************////
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadReferralTypeCriteria();
        await this.loadReferralTypeCriteria(true); // load all criteria, including inactive, for sorting purposes
        this.clear();
    }

    protected clear() {
        this.criterion = {};
    }

    protected buildCriterion(): CM.ReferralCriteria {
        const saveCriterion: CM.ReferralCriteria = {
            id: this.criterion?.id,
            description: this.criterion?.description,
            type: {
                id: this.$referralTypeId
            }
        };

        return saveCriterion;
    }

    protected openCriterionModal(criterion: CM.ReferralCriteria = {}) {
        this.criterion = criterion;
        this.$criterionModal.open();
    }

    protected closeCriterionModal() {
        this.$criterionModal.close();
        this.clear();
    }

    protected async submit(): Promise<void> {
        const criterionId = this.criterion.id
        await this.saveCriteria();
        this.$criterionModal.close();
        // reload grid and reset page
        this.reload();
        if(!criterionId) this.reloadReferralType(); // only reload referral type on add, not edit
    }

    protected openSortCriterionModal() {
        this.$sortCriteriaModal.open();
    }

    protected closeSortCriteriaModal(): void {
        if(this.$criteriaList.reset) this.$criteriaList.reset();
        this.$sortCriteriaModal.close();
    }

    protected async saveReferralTypeOrder(): Promise<void> {
        await this.sortCriteria();
        this.closeSortCriteriaModal();
        await this.reload();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadReferralTypeCriteria(forSort: boolean = false): Promise<void> {
        if(!this.$referralTypeId) return;
        const includes: string[] = [];
        const fields: string[] = ['id', '$display'];
        const options: Partial<IQueryLoadOptions> = forSort ? { includeInactive: true } :
            // options for standard load (includes paging)
            {
                top: this.pageSize,
                sorting: this.sorting,
                page: this.$pager?.page || 0,
                includeInactive: this.includeInactive
            };

        try {
            this.state.beginLoad();

            const response = await this.service.loadReferralTypeCriteria(this.$referralTypeId, options, includes, fields);
            if(forSort) {
                // construct list to keep track of criteria order
                this.criteriaOrder = response.results.map((item, index) => ({
                    id: item.id,
                    value: item.$display,
                    order: index,
                    isActive: item.isActive
                } as ReferralOrderItem));
            } else {
                this.referralTypeCriteria = response.results;
                this.resultCount = response.count || 0;
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateCriteria(criteria: CM.ReferralCriteria, isActive: boolean) {
        if(!criteria?.id) return;
        if(!isActive) {
            const result = await confirmation.showDeactivate('cm:referral.type.criteria.$singular');
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }
            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }

        try {
            this.state.beginSave();
            const response = await this.service.activateCriteria(criteria.id!, isActive);

            this.toaster.standard.activate('cm:referral.type.criteria.$singular', isActive, response.isSuccessful);
        } catch(error) {
            this.toaster.standard.activate('cm:referral.type.criteria.$singular', isActive, false);
            throw error;
        } finally {
            this.state.endSave();
            // reload grid and reset page
            this.reload();
            this.reloadReferralType();
        }
    }

    @apiCall
    private async saveCriteria(): Promise<void> {
        const criteria = this.buildCriterion();
        let isSuccessful = false;
        try {
            this.state.beginLoad();

            const response = await this.service.saveCriteria(criteria);
            isSuccessful = response.isSuccessful;
        } catch(error) {
            throw error;
        } finally {
            if(!!criteria.id) {
                this.toaster.standard.updated('cm:referral.type.criteria.$singular', isSuccessful);
            } else {
                this.toaster.standard.created('cm:referral.type.criteria.$singular', isSuccessful);
            }
            this.state.endLoad();
        }
    }

    @apiCall
    private async sortCriteria(): Promise<void> {
        let isSuccessful = false;
        try {
            this.state.beginLoad();

            const response = await this.service.sortCriteria({ id: this.$referralTypeId }, this.criteriaOrder.map(item => item.id));
            isSuccessful = response.isSuccessful;
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.sorted('cm:referral.type.criteria.$', isSuccessful);
            this.state.endLoad();
        }
    }
}
